import { Box, Heading, Text, VStack, UnorderedList, ListItem, Alert, AlertIcon, AlertDescription, Link as ChakraLink } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import MarketingLayout from '../components/marketing/MarketingLayout';

const Section = ({ title, children }) => (
  <Box>
    <Heading size="md" color="gray.900" mt={8} mb={3}>{title}</Heading>
    <VStack align="stretch" spacing={3} color="gray.700" lineHeight="1.8">{children}</VStack>
  </Box>
);

export const PrivacyPage = () => (
  <MarketingLayout title="Privacy Policy" subtitle="Last updated: 22 June 2026">
    <Alert status="warning" borderRadius="xl" mb={6} alignItems="flex-start">
      <AlertIcon />
      <AlertDescription fontSize="sm">
        This is a starting template, not legal advice. Have it reviewed by qualified counsel and tailored to your
        jurisdiction and operations before publishing.
      </AlertDescription>
    </Alert>

    <VStack align="stretch" spacing={2}>
      <Section title="1. Overview">
        <Text>
          StatFalcon (“we”, “us”) provides privacy-first web analytics and ad serving. This policy explains what data
          the platform processes and how. StatFalcon is offered two ways, and that affects who is responsible for the
          data:
        </Text>
        <UnorderedList spacing={1} pl={4}>
          <ListItem><strong>Cloud:</strong> we host the platform and act as a <em>processor</em> on behalf of our customers, who are the data controllers for their visitors' data.</ListItem>
          <ListItem><strong>Self-hosted:</strong> you run the software on your own infrastructure and are the sole <em>controller</em> and processor of all data you collect; we have no access to it.</ListItem>
        </UnorderedList>
      </Section>

      <Section title="2. Data we process">
        <Text>To measure audiences and serve ads, the platform may process:</Text>
        <UnorderedList spacing={1} pl={4}>
          <ListItem>Page URL, title, and referrer</ListItem>
          <ListItem>A truncated / anonymized IP address used for approximate geo and bot detection</ListItem>
          <ListItem>User-agent, derived device type, browser, and operating system</ListItem>
          <ListItem>A first-party visitor identifier and UTM campaign parameters</ListItem>
          <ListItem>Ad impressions, clicks, and frequency-capping counters</ListItem>
        </UnorderedList>
        <Text>We do not collect names, emails, or other directly identifying information through tracking, and we do not use third-party advertising cookies.</Text>
      </Section>

      <Section title="3. Cookies and local storage">
        <Text>
          StatFalcon is designed to operate without third-party cookies. A first-party identifier may be stored in the
          visitor's browser to distinguish new from returning visitors and to enforce ad frequency caps. Because no
          third-party tracking cookies are set, most deployments do not require a cookie-consent banner — but you
          should confirm this against your own legal requirements.
        </Text>
      </Section>

      <Section title="4. Legal bases (GDPR)">
        <Text>
          Where the GDPR applies, processing is generally based on the legitimate interests of the site operator in
          understanding audience behavior and operating advertising, balanced against visitor privacy through data
          minimization and IP anonymization — or on consent where required by local law.
        </Text>
      </Section>

      <Section title="5. Data retention">
        <Text>
          Cloud retention follows your plan's configured retention period. Self-hosted operators control their own
          retention entirely. Aggregated, non-identifying statistics may be kept longer.
        </Text>
      </Section>

      <Section title="6. Sub-processors (Cloud)">
        <Text>
          For Cloud accounts we rely on infrastructure and hosting providers to operate the service. A current list of
          sub-processors is available on request. Self-hosted deployments use no StatFalcon sub-processors.
        </Text>
      </Section>

      <Section title="7. Data sharing">
        <Text>
          We do not sell personal data and do not share visitor data with advertisers or third parties for their own
          marketing. Data is used only to provide analytics and ad serving to the operator who collected it.
        </Text>
      </Section>

      <Section title="8. Your rights">
        <Text>
          Depending on your jurisdiction (e.g. GDPR, CCPA), visitors may have rights to access, correct, delete, or
          restrict processing of their data. Because the site operator is the controller, such requests should be
          directed to the operator of the site you visited; for Cloud, we will assist our customers in fulfilling
          them.
        </Text>
      </Section>

      <Section title="9. Children">
        <Text>StatFalcon is not directed to children and does not knowingly process data from them.</Text>
      </Section>

      <Section title="10. Changes">
        <Text>We may update this policy from time to time. Material changes will be reflected by the “Last updated” date above.</Text>
      </Section>

      <Section title="11. Contact">
        <Text>
          Questions about this policy? <ChakraLink as={RouterLink} to="/contact" color="brand.600" fontWeight="600">Contact us</ChakraLink> or
          email <ChakraLink href="mailto:privacy@statfalcon.com" color="brand.600">privacy@statfalcon.com</ChakraLink>.
        </Text>
      </Section>
    </VStack>
  </MarketingLayout>
);

export default PrivacyPage;
